import { LayoutType } from '../../app/state/LayoutType';
import { SiteSpaceContentType } from '../../app/state/SiteSpaceContentTypeState';
import { ExplorableItemType } from '../../ts/models/ContentType';
import { FavoriteItemType } from '../../ts/ServerApi/types/IFavorite';
import { DisplayMode } from '../../ts/ServerApi/types/ISessionInfo';
import { BaseTelemetryEvent, ValidTelemetryEvent } from '../../ts/services/TelemetryService';
import { ExplorerEventInfo } from './TelemetryEventCreator';

/**
 * All the event schemata in this file must be registered in self-service-analytics.tsi.lan:
 *
 * https://mytableau.tableaucorp.com/display/devcomm/VizPortal+Client+telemetry+events
 */

export type ExplorerEvent = ExplorerEventInfo & BaseTelemetryEvent;

export enum TelemetryEvents {
  AdvancedFilterPanelToggle = 'AdvancedFilterPanelToggle',
  DevicePreviewHidePreview = 'DevicePreview_HidePreview',
  DevicePreviewPreviewStateChange = 'DevicePreview_PreviewStateChange',
  DevicePreviewShowPreview = 'DevicePreview_ShowPreview',
  ExplorerDisplayMode = 'ExplorerDisplayMode',
  ExplorerOpenDetail = 'ExplorerOpenDetail',
  ExplorerSort = 'ExplorerSort',
  FavoriteItemOpened = 'FavoriteItemOpened',
  FavoritesMenuNavigateToFavorite = 'FavoritesMenuNavigateToFavorite',
  FavoritesMenuOpen = 'FavoritesMenuOpen',
  PreviewDeviceLayoutsLinkClicked = 'PreviewDeviceLayoutsLinkClicked',
  PublishCompleteDialogDisplayed = 'PublishCompleteDialogDisplayed',
  RedirectSource = 'redirect_source',
  RecommendationTypeFilter = 'RecommendationTypeFilter',
  SearchResultClicked = 'SearchResultClicked',
  ShowExplorer = 'ShowExplorer',
  SiteSettingServerAdmin = 'SiteSettingServerAdmin',
  SiteSettingSiteAdmin = 'SiteSettingSiteAdmin',
  SiteSpaceContentTypeChange = 'SiteSpaceContentTypeChange',
  StarterClicked = 'StarterClicked',
  StartersDropdownConnectionTypeChanged = 'StartersDropdownConnectionTypeChanged',
  StarterWorkbookCreationClicked = 'StarterWorkbookCreationClicked',
  VizPortalNavigation = 'VizPortalNavigation',
  LeftNavLinkNavigation = 'LeftNavLinkNavigation',
  PageHeaderButtonClick = 'PageHeaderButtonClick',
  LeftNavCollapseExpandClick = 'LeftNavCollapseExpandClick',
  HomeApiPerformance = 'HomeApiPerformance',
  UnhandledError = 'UnhandledError',
  HomeSeeAllClick = 'HomeSeeAllClick',
  HomeWelcomeChannel = 'HomeWelcomeChannel',
  ContentClicked = 'ContentClicked',
  FavoriteToggleClicked = 'FavoriteToggleClicked',
  ActionMenuItemClicked = 'ActionMenuItemClicked',
  BuyLicenses = 'BuyLicenses',
  ShareDialogUserActions = 'ShareDialogUserActionsForVPC',
  UploadWorkbookEntryPoint = 'UploadWorkbookEntryPoint',
  UploadWorkbookDialogDetails = 'UploadWorkbookDialogDetails',
  UploadWorkbookActionDetails = 'UploadWorkbookActionDetails'
}

export type ContentTelemetryEvents = TelemetryEvents.ContentClicked | TelemetryEvents.FavoriteToggleClicked | TelemetryEvents.ActionMenuItemClicked;

export interface AdvancedFilterPanelToggleEvent extends ValidTelemetryEvent<AdvancedFilterPanelToggleEvent> {
  type: TelemetryEvents.AdvancedFilterPanelToggle;
  openOrClose: string;
  mixedContentEnabled: boolean;
}

export interface FavoriteItemOpenedEvent extends ValidTelemetryEvent<FavoriteItemOpenedEvent> {
  type: TelemetryEvents.FavoriteItemOpened;
  favoriteItemType: FavoriteItemType;
  favoriteItemId: string;
}

export interface FavoritesMenuOpenEvent extends ValidTelemetryEvent<FavoritesMenuOpenEvent> {
  type: TelemetryEvents.FavoritesMenuOpen;
  favoriteItemCount: number;
}

export interface FavoritesMenuNavigateToFavoriteEvent extends ValidTelemetryEvent<FavoritesMenuNavigateToFavoriteEvent> {
  type: TelemetryEvents.FavoritesMenuNavigateToFavorite;
  favoriteItemType: FavoriteItemType;
  favoriteItemId: string;
}

export interface PreviewDeviceLayoutsLinkClickedEvent extends ValidTelemetryEvent<PreviewDeviceLayoutsLinkClickedEvent> {
  type: TelemetryEvents.PreviewDeviceLayoutsLinkClicked;
}

export interface PublishCompleteDialogDisplayedEvent extends ValidTelemetryEvent<PublishCompleteDialogDisplayedEvent> {
  type: TelemetryEvents.PublishCompleteDialogDisplayed;
  isPreviewDeviceLayoutsLinkDisplayed: boolean;
}

export interface RedirectSourceEvent extends ValidTelemetryEvent<RedirectSourceEvent> {
  type: TelemetryEvents.RedirectSource;
  source: string;
}

export interface SiteSpaceContentTypeChangeEvent extends ValidTelemetryEvent<SiteSpaceContentTypeChangeEvent> {
  type: TelemetryEvents.SiteSpaceContentTypeChange;
  newContentType: SiteSpaceContentType;
  oldContentType: SiteSpaceContentType;
}

export interface StarterClicked extends ValidTelemetryEvent<StarterClicked> {
  connectionType: string;
  selectedStarterIndex: number;
  source: StartersDialogClickSourceOptions;
  starterId: string;
  starterName: string;
  type: TelemetryEvents.StarterClicked;
}

export interface StartersDropdownConnectionTypeChanged extends ValidTelemetryEvent<StartersDropdownConnectionTypeChanged> {
  connectionType: string;
  type: TelemetryEvents.StartersDropdownConnectionTypeChanged;
}

export interface StarterWorkbookCreationClicked extends ValidTelemetryEvent<StarterWorkbookCreationClicked> {
  connectionType: string;
  source: StarterWorkbookCreationSourceOptions;
  starterId: string;
  starterName: string;
  type: TelemetryEvents.StarterWorkbookCreationClicked;
  withSampleData: boolean;
}
export interface VizPortalNavigationEvent extends ValidTelemetryEvent<VizPortalNavigationEvent> {
  type: TelemetryEvents.VizPortalNavigation;
  newState: string;
  oldState: string;
  mixedContentEnabled: boolean;
}

export interface ExplorerSortEvent
  extends ValidTelemetryEvent<ExplorerEvent>, ValidTelemetryEvent<ExplorerSortEvent> {

  type: TelemetryEvents.ExplorerSort;
  sortField: string;
  ascending: boolean;
  eventSource: 'column' | 'dropdown';
}

export interface RecommendationTypeFilterEvent extends ValidTelemetryEvent<ExplorerEvent>, ValidTelemetryEvent<RecommendationTypeFilterEvent> {
  type: TelemetryEvents.RecommendationTypeFilter;
  recommendationType: string;
}

export interface ExplorerOpenDetailEvent extends ValidTelemetryEvent<ExplorerEvent>, ValidTelemetryEvent<ExplorerOpenDetailEvent> {
  type: TelemetryEvents.ExplorerOpenDetail;
  detailContentId: string;
  detailContentType: ExplorableItemType;
}

export interface ExplorerDisplayMode
  extends ValidTelemetryEvent<ExplorerEvent>, ValidTelemetryEvent<ExplorerDisplayMode> {

  type: TelemetryEvents.ExplorerDisplayMode;
  previousMode: DisplayMode;
  newMode: DisplayMode;
}

export interface ShowExplorer
  extends ValidTelemetryEvent<ExplorerEvent>, ValidTelemetryEvent<ShowExplorer> {
  type: TelemetryEvents.ShowExplorer;
}

export interface DevicePreviewShowPreviewEvent extends ValidTelemetryEvent<DevicePreviewShowPreviewEvent> {
  type: TelemetryEvents.DevicePreviewShowPreview;
}

// Currently unused, but exists to keep parity with a previously-existing unused event in the DeprecatedEventWrapper
export interface DevicePreviewHidePreviewEvent extends ValidTelemetryEvent<DevicePreviewHidePreviewEvent> {
  type: TelemetryEvents.DevicePreviewHidePreview;
}

export interface DevicePreviewPreviewStateChangeEvent extends ValidTelemetryEvent<DevicePreviewPreviewStateChangeEvent> {
  type: TelemetryEvents.DevicePreviewPreviewStateChange;
  preview_device_type: string;
}

export interface LeftNavLinkNavigationEvent extends ValidTelemetryEvent<LeftNavLinkNavigationEvent> {
  type: TelemetryEvents.LeftNavLinkNavigation;
  navLocation: string;
}

export interface PageHeaderButtonClickEvent extends ValidTelemetryEvent<PageHeaderButtonClickEvent> {
  type: TelemetryEvents.PageHeaderButtonClick;
  button: 'modal-left-nav' | 'back-button' | 'help-menu' | 'alert-menu' | 'user-menu' | 'viz-viewer-search' | 'data-connections-tooltip';
  action: 'open' | 'close';
}

export interface LeftNavCollapseExpandClickEvent extends ValidTelemetryEvent<LeftNavCollapseExpandClickEvent> {
  type: TelemetryEvents.LeftNavCollapseExpandClick;
  action: 'collapse' | 'expand';
  layoutType: LayoutType;
}

export interface SearchResultClickedEvent extends ValidTelemetryEvent<SearchResultClickedEvent> {
  type: TelemetryEvents.SearchResultClicked;
  eventSource: 'omni' | 'search-space';
  resultType: string;
  seeAll: boolean;
  index: number;
  total: number;
  searchTerm: string;
  contentId: string;
}

export interface UnhandledErrorTelemetryEvent extends ValidTelemetryEvent<UnhandledErrorTelemetryEvent> {
  type: TelemetryEvents.UnhandledError;
  error: string;
  errorInfo: string;
}

export interface SiteSettingServerAdminTelemetryEvent extends ValidTelemetryEvent<SiteSettingServerAdminTelemetryEvent> {
  type: TelemetryEvents.SiteSettingServerAdmin;
  adminCount_new: number;
  adminCount_old: number;
  authoringEnabled_new: boolean;
  authoringEnabled_old: boolean;
  availability_new: string;
  availability_old: string;
  cacheWarmupEnabled_new: boolean;
  cacheWarmupEnabled_old: boolean;
  catalogingOnSiteEnabled_new: boolean;
  catalogingOnSiteEnabled_old: boolean;
  contentVersionLimit_new: number;
  contentVersionLimit_old: number;
  contentVersionLimitEnabled_new: boolean;
  contentVersionLimitEnabled_old: boolean;
  currentLoggedInUserCount_new: number;
  currentLoggedInUserCount_old: number;
  customSubscriptionEmailEnabled_new: boolean;
  customSubscriptionEmailEnabled_old: boolean;
  customSubscriptionFooterEnabled_new: boolean;
  customSubscriptionFooterEnabled_old: boolean;
  flowsOnSiteEnabled_new: boolean;
  flowsOnSiteEnabled_old: boolean;
  guestAccessEnabled_new: boolean;
  guestAccessEnabled_old: boolean;
  licensedUserCount_new: number;
  licensedUserCount_old: number;
  creatorCount_new: number;
  creatorCount_old: number;
  explorerCount_new: number;
  explorerCount_old: number;
  viewerCount_new: number;
  viewerCount_old: number;
  metricsEnabled_new: boolean;
  metricsEnabled_old: boolean;
  siteAdminsCanAddUsers_new: boolean;
  siteAdminsCanAddUsers_old: boolean;
  storageQuotaBytes_new: number;
  storageQuotaBytes_old: number;
  storageQuotaEnabled_new: boolean;
  storageQuotaEnabled_old: boolean;
  storageQuotaPercentUsed_new: number;
  storageQuotaPercentUsed_old: number;
  storageUsedBytes_new: number;
  storageUsedBytes_old: number;
  userCount_new: number;
  userCount_old: number;
  userQuota_new: number;
  userQuota_old: number;
  userQuotaMode_new: string;
  userQuotaMode_old: string;
  versionHistoryEnabled_new: boolean;
  versionHistoryEnabled_old: boolean;
  runNowEnabled_new: boolean;
  runNowEnabled_old: boolean;
  eventid: string;
  siteId: string;
  tierCreatorCapacity_new: number;
  tierCreatorCapacity_old: number;
  tierExplorerCapacity_new: number;
  tierExplorerCapacity_old: number;
  tierViewerCapacity_new: number;
  tierViewerCapacity_old: number;
}

export interface SiteSettingSiteAdminTelemetryEvent extends ValidTelemetryEvent<SiteSettingSiteAdminTelemetryEvent> {
  type: TelemetryEvents.SiteSettingSiteAdmin;
  addSupportUserEnabled_new: boolean;
  addSupportUserEnabled_old: boolean;
  allowSubscriptionAttachments_new?: boolean;
  allowSubscriptionAttachments_old?: boolean;
  alternativeFederationEngineEnabled_new: boolean;
  alternativeFederationEngineEnabled_old: boolean;
  askDataMode_new: string;
  askDataMode_old: string;
  commentingEnabled_new?: boolean;
  commentingEnabled_old?: boolean;
  commentingMentionsEnabled_new?: boolean;
  commentingMentionsEnabled_old?: boolean;
  customScheduleSubscriptionForSiteEnabled_new?: boolean;
  customScheduleSubscriptionForSiteEnabled_old?: boolean;
  dataAlertsEnabled_new: boolean;
  dataAlertsEnabled_old: boolean;
  derivedPermissionsEnabled_new: boolean;
  derivedPermissionsEnabled_old: boolean;
  extractEncryptionMode_new: string;
  extractEncryptionMode_old: string;
  isAdminDataSourceEnabled_new: boolean;
  isAdminDataSourceEnabled_old: boolean;
  namedSharingEnabled_new: boolean;
  namedSharingEnabled_old: boolean;
  notificationEnabled_new: boolean;
  notificationEnabled_old: boolean;
  notificationForClientNotRunningEnabled_new?: boolean;
  notificationForClientNotRunningEnabled_old?: boolean;
  refreshTokenEnabled_new: boolean;
  refreshTokenEnabled_old: boolean;
  remoteQueryingEnabled_new: boolean;
  remoteQueryingEnabled_old: boolean;
  requestAccessEnabled_new: boolean;
  requestAccessEnabled_old: boolean;
  sandboxDatasourcesEnabled_new: boolean;
  sandboxDatasourcesEnabled_old: boolean;
  sandboxEnabled_new: boolean;
  sandboxEnabled_old: boolean;
  sandboxFlowsEnabled_new: boolean;
  sandboxFlowsEnabled_old: boolean;
  sandboxStorageQuota_new: number;
  sandboxStorageQuota_old: number;
  sandboxStorageQuotaSet_new: boolean;
  sandboxStorageQuotaSet_old: boolean;
  serverAllowSubscriptionAttachments_new?: boolean;
  serverAllowSubscriptionAttachments_old?: boolean;
  sheetImageEnabled_new: boolean;
  sheetImageEnabled_old: boolean;
  siteInviteEmailEnabled_new: boolean;
  siteInviteEmailEnabled_old: boolean;
  subscribeOthersEnabled_new?: boolean;
  subscribeOthersEnabled_old?: boolean;
  subscriptionsEnabled_new?: boolean;
  subscriptionsEnabled_old?: boolean;
  userVisibility_new: string;
  userVisibility_old: string;
  vizRecommendationsEnabled_new: boolean;
  vizRecommendationsEnabled_old: boolean;
  vizRecUsernameVisibilityEnabled_new: boolean;
  vizRecUsernameVisibilityEnabled_old: boolean;
  eventid: string;
  siteId: string;
}

export interface BuyLicensesTelemetryEvent
  extends ValidTelemetryEvent<BuyLicensesTelemetryEvent> {
  type: TelemetryEvents.BuyLicenses;
  action: 'buy-licenses-button-1-displayed' | 'buy-licenses-button-1-clicked' |
        'buy-licenses-button-2-displayed' | 'buy-licenses-button-2-clicked' |
        'buy-licenses-button-1-submit' | 'buy-licenses-button-2-submit';
}

export enum ShareDialogUserActionOptions {
  CloseShareDialog = 'Close',
  CopyEmbedCode = 'Copy embed code',
  CopyLink = 'Copy link',
  OpenShareDialog = 'Open',
  ShareContent = 'Share content',
  ContentCleared = 'Content cleared'
}

export enum StartersDialogClickSourceOptions {
  Dialog = 'Dialog',
  HomeCard = 'HomeCard',
  HomeCardMoreLikeThis = 'HomeCardMoreLikeThis',
  Space = 'Space',
}

export enum StarterWorkbookCreationSourceOptions {
  Dialog = 'Dialog',
  Space = 'Space',
}

export interface ShareDialogUserActionsEvent extends ValidTelemetryEvent<ShareDialogUserActionsEvent> {
  // Ensure that property names matches exactly with the schema including their CASE
  // otherwise events will not be recorded
  type: TelemetryEvents.ShareDialogUserActions;
  UserActionType: ShareDialogUserActionOptions;
  Args?: string;
}
